/**
 * Offline-Aware React Hooks
 * Provides hooks for offline data fetching and mutations with caching support
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from '@tanstack/react-query';
import {
  OfflineState,
  SyncConflict,
  CacheStats,
  OperationType,
  SyncResult,
  QueuedOperation,
} from './types';

// Platform detection
const isTauri = typeof window !== 'undefined' && '__TAURI__' in window;

// Dynamic import for platform-specific service
let offlineService: typeof import('./desktopOfflineService').desktopOfflineService | null = null;

async function getOfflineService() {
  if (offlineService) return offlineService;

  if (isTauri) {
    const module = await import('./desktopOfflineService');
    offlineService = module.desktopOfflineService;
  }

  return offlineService;
}

// ============================================================================
// useOfflineState Hook
// ============================================================================

/**
 * Hook to access and subscribe to offline state
 */
export function useOfflineState(): OfflineState {
  const [state, setState] = useState<OfflineState>({
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    forcedOffline: false,
    syncStatus: 'idle',
    lastSyncAt: null,
    pendingOperations: 0,
    conflicts: [],
    error: null,
  });

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    async function setup() {
      const service = await getOfflineService();
      if (service) {
        unsubscribe = service.subscribe(setState);
      } else {
        // Fallback for non-Tauri (web browser)
        const handleOnline = () =>
          setState((s) => ({ ...s, isOnline: true }));
        const handleOffline = () =>
          setState((s) => ({ ...s, isOnline: false, syncStatus: 'offline' }));

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        unsubscribe = () => {
          window.removeEventListener('online', handleOnline);
          window.removeEventListener('offline', handleOffline);
        };
      }
    }

    setup();

    return () => {
      unsubscribe?.();
    };
  }, []);

  return state;
}

// ============================================================================
// useOfflineControls Hook — manual toggle + queue introspection (desktop)
// ============================================================================

/**
 * Imperative offline-aware mutation for save handlers that aren't react-query
 * mutations. On desktop (Tauri) when offline it queues the op + optimistically
 * caches; online (or on web) it just runs `fn`. Returns `{ queued: true }` when
 * the write was queued for later sync.
 */
export async function offlineAwareMutate<T>(
  operationType: OperationType,
  entityType: string,
  entityId: string | undefined,
  payload: unknown,
  fn: () => Promise<T>,
): Promise<T | { queued: true }> {
  const service = await getOfflineService();
  if (service) {
    return service.mutateWithOfflineSupport(
      operationType,
      entityType,
      entityId,
      payload,
      fn,
    ) as Promise<T | { queued: true }>;
  }
  return fn();
}

export function useOfflineControls() {
  const setForcedOffline = useCallback(async (value: boolean) => {
    const service = await getOfflineService();
    await service?.setForcedOffline(value);
  }, []);

  const getAllOperations = useCallback(async (): Promise<QueuedOperation[]> => {
    const service = await getOfflineService();
    return (await service?.getAllOperations()) ?? [];
  }, []);

  return { isDesktop: isTauri, setForcedOffline, getAllOperations };
}

// ============================================================================
// useOfflineQuery Hook
// ============================================================================

interface UseOfflineQueryOptions<T> extends Omit<UseQueryOptions<T>, 'queryKey' | 'queryFn'> {
  entityType: string;
  entityId: string;
  ttlSeconds?: number;
  offlineEnabled?: boolean;
}

/**
 * Query hook with offline caching support
 */
export function useOfflineQuery<T>(
  queryKey: string[],
  fetchFn: () => Promise<T>,
  options: UseOfflineQueryOptions<T>
) {
  const { entityType, entityId, ttlSeconds, offlineEnabled = true, ...queryOptions } = options;
  const { isOnline } = useOfflineState();

  return useQuery({
    queryKey,
    queryFn: async () => {
      const service = await getOfflineService();

      if (service && offlineEnabled) {
        return service.fetchWithOfflineSupport(
          entityType,
          entityId,
          fetchFn,
          { ttlSeconds }
        );
      }

      // Fallback: direct fetch
      return fetchFn();
    },
    // When offline, use stale data indefinitely
    staleTime: isOnline ? (queryOptions.staleTime ?? 5 * 60 * 1000) : Infinity,
    gcTime: offlineEnabled ? Infinity : (queryOptions.gcTime ?? 30 * 60 * 1000),
    ...queryOptions,
  });
}

// ============================================================================
// useOfflineMutation Hook
// ============================================================================

interface UseOfflineMutationOptions<TData, TVariables>
  extends Omit<UseMutationOptions<TData, Error, TVariables>, 'mutationFn'> {
  entityType: string;
  operationType: OperationType;
  getEntityId?: (variables: TVariables) => string | undefined;
  optimisticUpdate?: (variables: TVariables) => void;
  invalidateQueries?: string[][];
}

/**
 * Mutation hook with offline queue support
 */
export function useOfflineMutation<TData, TVariables>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options: UseOfflineMutationOptions<TData, TVariables>
) {
  const {
    entityType,
    operationType,
    getEntityId,
    optimisticUpdate,
    invalidateQueries,
    ...mutationOptions
  } = options;

  const queryClient = useQueryClient();
  const { isOnline } = useOfflineState();

  return useMutation({
    mutationFn: async (variables: TVariables) => {
      const service = await getOfflineService();
      const entityId = getEntityId?.(variables);

      if (service) {
        const result = await service.mutateWithOfflineSupport(
          operationType,
          entityType,
          entityId,
          variables,
          mutationFn
        );

        // Check if it was queued
        if (result && typeof result === 'object' && 'queued' in result) {
          // Apply optimistic update
          optimisticUpdate?.(variables);
          return result as unknown as TData;
        }

        return result;
      }

      // Fallback: direct mutation
      return mutationFn(variables);
    },
    onSuccess: (data, variables, context) => {
      // Invalidate related queries
      if (invalidateQueries) {
        invalidateQueries.forEach((queryKey) => {
          queryClient.invalidateQueries({ queryKey });
        });
      }

      mutationOptions.onSuccess?.(data, variables, context);
    },
    ...mutationOptions,
  });
}

// ============================================================================
// useSync Hook
// ============================================================================

interface UseSyncReturn {
  syncNow: () => Promise<SyncResult>;
  isSyncing: boolean;
  lastSyncAt: number | null;
  pendingCount: number;
  error: string | null;
}

/**
 * Hook for manual sync control
 */
export function useSync(): UseSyncReturn {
  const state = useOfflineState();
  const [isSyncing, setIsSyncing] = useState(false);

  const syncNow = useCallback(async (): Promise<SyncResult> => {
    setIsSyncing(true);
    try {
      const service = await getOfflineService();
      if (service) {
        return await service.syncNow();
      }
      return {
        succeeded: 0,
        failed: 0,
        conflicts: [],
        timestamp: Date.now(),
      };
    } finally {
      setIsSyncing(false);
    }
  }, []);

  return {
    syncNow,
    isSyncing: isSyncing || state.syncStatus === 'syncing',
    lastSyncAt: state.lastSyncAt,
    pendingCount: state.pendingOperations,
    error: state.error,
  };
}

// ============================================================================
// useConflicts Hook
// ============================================================================

interface UseConflictsReturn {
  conflicts: SyncConflict[];
  resolveConflict: (
    conflictId: string,
    resolution: 'client' | 'server' | 'merged',
    mergedData?: unknown
  ) => Promise<void>;
  hasConflicts: boolean;
}

/**
 * Hook for managing sync conflicts
 */
export function useConflicts(): UseConflictsReturn {
  const state = useOfflineState();

  const resolveConflict = useCallback(
    async (
      conflictId: string,
      resolution: 'client' | 'server' | 'merged',
      mergedData?: unknown
    ) => {
      const service = await getOfflineService();
      if (service) {
        await service.resolveConflict(conflictId, resolution, mergedData);
      }
    },
    []
  );

  return {
    conflicts: state.conflicts,
    resolveConflict,
    hasConflicts: state.conflicts.length > 0,
  };
}

// ============================================================================
// useCacheStats Hook
// ============================================================================

/**
 * Hook to get cache statistics
 */
export function useCacheStats(): {
  stats: CacheStats | null;
  loading: boolean;
  refresh: () => Promise<void>;
  clearCache: () => Promise<void>;
  clearExpired: () => Promise<number>;
} {
  const [stats, setStats] = useState<CacheStats | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const service = await getOfflineService();
      if (service) {
        const cacheStats = await service.getCacheStats();
        setStats(cacheStats);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const clearCache = useCallback(async () => {
    const service = await getOfflineService();
    if (service) {
      await service.clearAllCache();
      await refresh();
    }
  }, [refresh]);

  const clearExpired = useCallback(async () => {
    const service = await getOfflineService();
    if (service) {
      const count = await service.clearExpiredCache();
      await refresh();
      return count;
    }
    return 0;
  }, [refresh]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { stats, loading, refresh, clearCache, clearExpired };
}

// ============================================================================
// useOfflineEntity Hook
// ============================================================================

interface UseOfflineEntityOptions {
  ttlSeconds?: number;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

/**
 * Hook to manage a single offline entity
 */
export function useOfflineEntity<T>(
  entityType: string,
  entityId: string,
  fetchFn: () => Promise<T>,
  options?: UseOfflineEntityOptions
) {
  const { ttlSeconds, autoRefresh = false, refreshInterval = 60000 } = options ?? {};

  const queryKey = useMemo(
    () => [entityType, entityId],
    [entityType, entityId]
  );

  const query = useOfflineQuery<T>(queryKey, fetchFn, {
    entityType,
    entityId,
    ttlSeconds,
    refetchInterval: autoRefresh ? refreshInterval : undefined,
  });

  const updateMutation = useOfflineMutation<T, Partial<T>>(
    async (updates) => {
      // This would call the actual API update endpoint
      const response = await fetch(`/api/${entityType}/${entityId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      return response.json();
    },
    {
      entityType,
      operationType: 'UPDATE',
      getEntityId: () => entityId,
      invalidateQueries: [queryKey],
    }
  );

  const deleteMutation = useOfflineMutation<void, void>(
    async () => {
      await fetch(`/api/${entityType}/${entityId}`, {
        method: 'DELETE',
      });
    },
    {
      entityType,
      operationType: 'DELETE',
      getEntityId: () => entityId,
      invalidateQueries: [queryKey],
    }
  );

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    update: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    remove: deleteMutation.mutateAsync,
    isRemoving: deleteMutation.isPending,
  };
}

// ============================================================================
// useOfflineList Hook
// ============================================================================

interface UseOfflineListOptions {
  ttlSeconds?: number;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

/**
 * Hook to manage a list of offline entities
 */
export function useOfflineList<T>(
  entityType: string,
  listId: string,
  fetchFn: () => Promise<T[]>,
  options?: UseOfflineListOptions
) {
  const { ttlSeconds, autoRefresh = false, refreshInterval = 60000 } = options ?? {};

  const queryKey = useMemo(
    () => [entityType, 'list', listId],
    [entityType, listId]
  );

  const query = useOfflineQuery<T[]>(queryKey, fetchFn, {
    entityType,
    entityId: `list:${listId}`,
    ttlSeconds,
    refetchInterval: autoRefresh ? refreshInterval : undefined,
  });

  const createMutation = useOfflineMutation<T, Omit<T, 'id'>>(
    async (newItem) => {
      const response = await fetch(`/api/${entityType}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newItem),
      });
      return response.json();
    },
    {
      entityType,
      operationType: 'CREATE',
      invalidateQueries: [queryKey],
    }
  );

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    create: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
  };
}

// ============================================================================
// Context Provider
// ============================================================================

import React, { createContext, useContext, ReactNode } from 'react';

interface OfflineContextValue {
  isInitialized: boolean;
  initialize: () => Promise<void>;
  state: OfflineState;
}

const OfflineContext = createContext<OfflineContextValue | null>(null);

export function OfflineProvider({ children }: { children: ReactNode }) {
  const [isInitialized, setIsInitialized] = useState(false);
  const state = useOfflineState();

  const initialize = useCallback(async () => {
    if (isInitialized) return;

    try {
      const service = await getOfflineService();
      if (service) {
        await service.initialize();
      }
      setIsInitialized(true);
    } catch (error) {
      console.error('Failed to initialize offline service:', error);
    }
  }, [isInitialized]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const value = useMemo(
    () => ({
      isInitialized,
      initialize,
      state,
    }),
    [isInitialized, initialize, state]
  );

  return React.createElement(
    OfflineContext.Provider,
    { value },
    children
  );
}

export function useOfflineContext(): OfflineContextValue {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOfflineContext must be used within OfflineProvider');
  }
  return context;
}
